<template>
    <div>
        <!-- Search Form -->
        <form v-if="!searched" @submit.prevent="submit">
            <div class="text-center mb-4">
                <div class="mx-auto mb-3" style="width: 56px; height: 56px; background: linear-gradient(135deg, #f0f4ff, #e0e8ff); border-radius: 50%; display: flex; align-items: center; justify-content: center;">
                    <img style="height: 32px" src="/img/zetdc-logo.webp" alt="ZETDC Logo">
                </div>
                <h5 class="font-weight-bold mb-1">ZESA View Token</h5>
                <p class="text-muted mb-0" style="font-size: 0.85rem;">Enter your meter number to retrieve your token purchase history</p>
            </div>

            <div class="form-group">
                <label class="font-weight-bold" style="font-size: 0.85rem;">Meter Number</label>
                <div class="input-group">
                    <div class="input-group-prepend">
                        <span class="input-group-text" style="background: #f8f9fa; border-right: none;">
                            <i v-if="form.loading" class="fas fa-spinner fa-pulse text-primary"></i>
                            <i v-else class="fas fa-tachometer-alt text-muted"></i>
                        </span>
                    </div>
                    <input
                        v-model="form.meter"
                        required
                        type="number"
                        :class="['form-control', form.errors.get('meter') ? 'is-invalid' : '']"
                        placeholder="e.g. 04012345678"
                        style="border-left: none;"
                    >
                    <div v-text="form.errors.get('meter')" class="invalid-feedback"></div>
                </div>
            </div>

            <div class="form-group mb-0">
                <button
                    type="submit"
                    :disabled="form.loading || !form.meter"
                    :class="['btn btn-block shadow-none', form.meter ? 'btn-primary' : 'btn-secondary']"
                    style="padding: 10px; font-size: 0.95rem;"
                >
                    <i v-if="form.loading" class="fas fa-spinner fa-pulse mr-1"></i>
                    <span v-else><i class="fas fa-bolt mr-1"></i></span>
                    View My Tokens
                </button>
            </div>
        </form>

        <!-- Results Section -->
        <div v-if="searched" class="slide-in-left">

            <!-- Tokens found -->
            <div v-if="tokens.length > 0">

                <div class="text-center py-2 mb-3" style="font-size: 0.8rem; color: #8b95a5;">
                    <i class="fas fa-info-circle mr-1"></i>
                    Only shows tokens purchased via <strong>Magetsi</strong> (website &amp; WhatsApp).
                </div>

                <!-- Meter header -->
                <div class="d-flex align-items-center p-3 mb-3 rounded" style="background: linear-gradient(135deg, #f0f7ff, #e8f0fe);">
                    <div class="mr-3" style="width: 40px; height: 40px; background: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
                        <img style="height: 22px" src="/img/zetdc-logo.webp" alt="ZETDC">
                    </div>
                    <div style="min-width: 0;">
                        <div class="font-weight-bold" style="font-size: 1.1rem; letter-spacing: 0.5px;">
                            <i class="fas fa-tachometer-alt mr-1 text-primary"></i>{{ meter }}
                        </div>
                    </div>
                </div>

                <!-- Summary bar -->
                <div class="d-flex justify-content-between align-items-center mb-3 px-1">
                    <span style="font-size: 0.8rem;" class="text-muted">
                        <i class="fas fa-receipt mr-1"></i>
                        Showing your <strong>{{ tokens.length }}</strong> most recent token{{ tokens.length !== 1 ? 's' : '' }}
                    </span>
                    <span v-if="totalPages > 1" style="font-size: 0.8rem;" class="text-muted">
                        {{ pageRangeStart }}-{{ pageRangeEnd }} of {{ tokens.length }}
                    </span>
                </div>

                <!-- Token cards -->
                <div class="token-list">
                    <div v-for="(token, index) in paginatedTokens" :key="index"
                         class="token-card mb-2">
                        <div class="d-flex justify-content-between align-items-center mb-2">
                            <span class="token-amount">
                                {{ formatCurrency(token.currency) }} {{ formatAmount(token.amount) }}
                            </span>
                            <span class="token-date">
                                <i class="far fa-calendar-alt mr-1"></i>{{ formatDate(token.created_at) }}
                            </span>
                        </div>
                        <div class="token-value-wrapper">
                            <div class="d-flex justify-content-between align-items-center mb-1">
                                <small class="text-uppercase" style="font-size: 0.65rem; letter-spacing: 0.5px; color: #8b95a5;">Token Number</small>
                                <small v-if="token.energy" class="text-muted" style="font-size: 0.75rem;"><i class="fas fa-bolt text-warning mr-1"></i>{{ token.energy }} kWh</small>
                            </div>
                            <div class="d-flex justify-content-between align-items-center">
                                <code class="token-number mb-0">{{ token.token }}</code>
                                <button @click="copyToken(token.token)" class="btn btn-sm btn-outline-secondary shadow-none ml-2 copy-btn" title="Copy token">
                                    <i :class="copiedToken === token.token ? 'fas fa-check text-success' : 'far fa-copy'"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Pagination -->
                <div v-if="totalPages > 1" class="d-flex justify-content-between align-items-center mt-3 mb-3">
                    <button
                        @click="currentPage--"
                        :disabled="currentPage === 1"
                        class="btn btn-sm btn-outline-primary shadow-none px-3"
                    >
                        <i class="fas fa-chevron-left mr-1"></i> Prev
                    </button>
                    <div class="d-flex align-items-center">
                        <span v-for="page in totalPages" :key="page"
                              @click="currentPage = page"
                              :class="['page-dot', currentPage === page ? 'active' : '']"
                        ></span>
                    </div>
                    <button
                        @click="currentPage++"
                        :disabled="currentPage === totalPages"
                        class="btn btn-sm btn-outline-primary shadow-none px-3"
                    >
                        Next <i class="fas fa-chevron-right ml-1"></i>
                    </button>
                </div>

                <div class="alert alert-warning text-center mb-3 py-2" style="font-size: 0.85rem;">
                    <i class="fas fa-exclamation-circle mr-1"></i>
                    This tool only shows tokens purchased via <strong>Magetsi</strong> (website &amp; WhatsApp chatbot).
                    <router-link to="/zesa" class="d-block mt-1 font-weight-bold">Buy ZESA on Magetsi to access this feature <i class="fas fa-arrow-right ml-1"></i></router-link>
                </div>

                <div class="cta-card text-center mb-3">
                    <p class="mb-2" style="font-size: 0.9rem;">
                        <strong>Never lose a token again.</strong> Buy ZESA on Magetsi and access your tokens anytime.
                    </p>
                    <router-link to="/zesa" class="btn btn-light btn-sm font-weight-bold shadow-none">
                        <i class="fas fa-bolt mr-1 text-warning"></i> Buy ZESA Now
                    </router-link>
                </div>
            </div>

            <!-- No tokens found -->
            <div v-else class="text-center">
                <div class="py-4">
                    <div class="mx-auto mb-3" style="width: 64px; height: 64px; background: #f5f5f5; border-radius: 50%; display: flex; align-items: center; justify-content: center;">
                        <i class="fas fa-search fa-lg text-muted"></i>
                    </div>
                    <h5 class="font-weight-bold mb-2">No Tokens Found</h5>
                    <p class="text-muted mb-3" style="font-size: 0.85rem;">
                        Meter <strong>{{ meter }}</strong> has no purchase history on Magetsi.
                    </p>
                    <div class="text-left p-3 rounded" style="font-size: 0.85rem; background: #f8f9fa;">
                        <p class="mb-2"><i class="fas fa-info-circle text-primary mr-1"></i> <strong>Please note:</strong> This tool only shows tokens purchased through the Magetsi platform.</p>
                        <p class="mb-0">Start buying ZESA through Magetsi and <strong>never lose a token again!</strong></p>
                    </div>

                    <div class="cta-card text-center mt-3">
                        <h6 class="font-weight-bold mb-2"><i class="fas fa-bolt text-warning mr-1"></i> Start Your Token History</h6>
                        <p style="font-size: 0.85rem;" class="mb-3">
                            Buy your first ZESA token on Magetsi — instant delivery, saved history, and 24/7 support.
                        </p>
                        <div class="d-flex flex-column flex-sm-row justify-content-center align-items-center">
                            <router-link to="/zesa" class="btn btn-light btn-sm font-weight-bold shadow-none mb-2 mb-sm-0 mr-sm-2">
                                <i class="fas fa-bolt mr-1 text-warning"></i> Buy ZESA Online
                            </router-link>
                            <a href="https://api.whatsapp.com/send/?phone=263785832033&text=Hi+Magetsi" target="_blank" class="btn btn-success btn-sm font-weight-bold shadow-none">
                                <i class="fab fa-whatsapp mr-1"></i> Buy on WhatsApp
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <button @click="reset" class="btn btn-outline-secondary btn-block mt-2 shadow-none" style="font-size: 0.9rem;">
                <i class="fas fa-redo mr-1"></i> Search Another Meter
            </button>
        </div>
    </div>
</template>

<script>
import Form from "../../../../core/forms/form";

export default {
    name: "ViewToken",
    data() {
        return {
            searched: false,
            tokens: [],
            meter: '',
            copiedToken: null,
            currentPage: 1,
            perPage: 5,
            form: new Form({
                meter: '',
            }),
        };
    },
    computed: {
        totalPages() {
            return Math.ceil(this.tokens.length / this.perPage);
        },
        paginatedTokens() {
            const start = (this.currentPage - 1) * this.perPage;
            return this.tokens.slice(start, start + this.perPage);
        },
        pageRangeStart() {
            return (this.currentPage - 1) * this.perPage + 1;
        },
        pageRangeEnd() {
            return Math.min(this.currentPage * this.perPage, this.tokens.length);
        },
    },
    methods: {
        submit() {
            this.form.submit('/zesa/view-tokens').then((response) => {
                var body = response.data.body || response.data;
                var tokens = body.tokens || [];
                var meter = body.meter || this.form.meter;

                this.tokens = tokens;
                this.meter = meter;
                this.currentPage = 1;
                this.$nextTick(() => {
                    this.searched = true;
                });
            }).catch(() => {});
        },
        reset() {
            this.form.meter = '';
            this.searched = false;
            this.tokens = [];
            this.meter = '';
            this.copiedToken = null;
            this.currentPage = 1;
        },
        formatCurrency(currency) {
            var map = { 'USD': 'US$', 'ZWG': 'ZiG', 'ZWL': 'ZWL' };
            return map[currency] || currency || 'US$';
        },
        formatAmount(amount) {
            if (!amount) return '0.00';
            return parseFloat(amount).toFixed(2);
        },
        formatToken(token) {
            if (!token) return '';
            if (!token.includes('#')) {
                return token.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();
            }
            return token.split('#').map(t =>
                t.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim()
            ).join('  |  ');
        },
        formatDate(date) {
            if (!date) return '';
            const d = new Date(date);
            return d.toLocaleDateString('en-GB', {
                day: 'numeric', month: 'short', year: 'numeric'
            });
        },
        copyToken(token) {
            if (!token) return;
            navigator.clipboard.writeText(token.replace(/\s/g, '')).then(() => {
                this.copiedToken = token;
                setTimeout(() => { this.copiedToken = null; }, 2000);
            });
        },
    },
};
</script>

<style scoped>
@keyframes slideInLeft {
    from {
        transform: translateX(-20px);
        opacity: 0;
    }
    to {
        transform: translateX(0);
        opacity: 1;
    }
}
.slide-in-left {
    animation: slideInLeft 0.35s ease-out;
}
.token-card {
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 10px;
    padding: 14px 16px;
    transition: box-shadow 0.2s;
}
.token-card:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
.token-amount {
    font-weight: 700;
    font-size: 0.95rem;
    color: #2d7a4f;
}
.token-date {
    font-size: 0.75rem;
    color: #8b95a5;
}
.token-value-wrapper {
    background: #f8f9fb;
    border-radius: 8px;
    padding: 10px 12px;
}
.token-number {
    font-size: 0.9rem;
    font-weight: 600;
    color: #2c3e50;
    word-break: break-all;
    line-height: 1.5;
    flex: 1;
}
.copy-btn {
    border-radius: 6px;
    padding: 4px 8px;
    font-size: 0.75rem;
    min-width: 32px;
}
.page-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #d4d9e0;
    margin: 0 3px;
    cursor: pointer;
    transition: all 0.2s;
}
.page-dot.active {
    background: #4a7cfb;
    transform: scale(1.3);
}
.cta-card {
    background: linear-gradient(135deg, #4a7cfb, #3862d6);
    color: #fff;
    border-radius: 10px;
    padding: 20px;
}
</style>
